import { Facebook } from 'almanac-ui/icons'
import { object, string } from 'yup'
import { FormSchema } from '../Wizard/Form/Types'
import { topic } from './shared/topic'
import { brandVoiceId, tone } from './shared/voiceTones'
import { avoid } from './shared/avoid'
import { callToAction } from './shared/callToAction'
import { promotion } from './shared/promotion'
import { project } from './shared/project'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { Tone } from 'components/DocRightRail/screens/AIAssistant/ToneSelect'
import { AlmanacFile } from 'types'
import { safify } from 'components/Wizards/schemas/utils/safify'
import { facebookExternalIntegrationId } from './shared/externalIntegrationId'
import { captionCount } from './shared/captionCount'
import { includeImages } from './shared/includeImages'
import { PostLength } from '../ContentPlanning/utils'
import { sourceMaterialIds } from './shared/sourceMaterials'
import { MultiStepSchema } from './Types'
import { brandVisualKitId } from './shared/brandVisualKit'
import { applyBrand } from './shared/applyBrand'
import { MediaType, mediaType } from './shared/mediaType'
import { brandKitId } from './shared/brandKitId'
import { language } from './shared/language'
import { TextChannels } from '../../../constants'

export type Values = {
  topic?: string
  caption_length?: string
  caption_count?: string
  filename?: string
  brand_voice_id?: number
  tone?: Array<Tone>
  language?: string
  project?: AlmanacFile
  source_material_ids?: number[]
  call_to_action?: string
  promotion?: string
  author_persona?: string
  product_description?: string
  avoid?: string
  include_images?: boolean
  additional_info?: string
  external_integration_id?: number
}

const meta = {
  type: GenerationType.FacebookPlanning,
  title: TextChannels[GenerationType.FacebookPlanning].name,
  steps: ['Topic', 'Posts', 'Plan'],
  submitLabel: 'Generate Posts',
  icon: Facebook,
  previewUrl:
    'https://res.cloudinary.com/almanac/image/upload/v1710183792/Home%20Card%20Previews/guxh6oo1ezhztatbgnik.png',
}

const fields = {
  topic: topic.meta({ label: 'What would you like to write about?', generationType: meta.type }),
  filename: string().default('').meta({ hide: true, autoFill: true }).transform(safify),
  media_type: mediaType.default(MediaType.Design),
  project: project.meta({ hide: true, autoFill: true }),
  caption_length: string()
    .required()
    .oneOf([
      PostLength.FiveToTenWords,
      PostLength.TenToTwentyFiveWords,
      PostLength.TwentyFiveToFiftyWords,
      PostLength.FiftyToSeventyFiveWords,
      PostLength.SeventyFivetoOneHundredWords,
      PostLength.OneHudredPlusWords,
    ])
    .default(PostLength.TenToTwentyFiveWords)
    .meta({ label: 'How long should each post be?', autoFill: true })
    .transform(safify),
  caption_count: captionCount,
  tone,
  include_images: includeImages.meta({ hide: true }),
  brand_voice_id: brandVoiceId,
  brand_kit_id: brandKitId,
  source_material_ids: sourceMaterialIds,
  external_integration_id: facebookExternalIntegrationId,
  language,
  promotion: promotion.meta({ collapsible: true, autoFill: true }),
  call_to_action: callToAction.meta({ collapsible: true, autoFill: true }),
  avoid: avoid.meta({ collapsible: true, autoFill: true }),
}

export const FacebookPlanningSchema = object(fields).meta(meta) as FormSchema<GenerationType | ChannelType>

export const MultiStepFacebookPlanningSchema = {
  meta: {
    type: GenerationType.FacebookPlanning,
    channel: ChannelType.FacebookPost,
    title: TextChannels[GenerationType.FacebookPlanning].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183792/Home%20Card%20Previews/guxh6oo1ezhztatbgnik.png',
    icon: Facebook,
  },
  steps: [
    FacebookPlanningSchema.meta({ title: 'Topic', submitLabel: 'Generate Posts' }),
    object({}).meta({
      type: ChannelType.FacebookPost,
      title: 'Posts',
      submitLabel: 'Plan',
    }),
    object({
      brand_visual_kit_id: brandVisualKitId,
      apply_brand: applyBrand,
    }).meta({
      type: ChannelType.FacebookPost,
      title: 'Plan',
      submitLabel: 'Generate Posts',
    }),
  ],
} as MultiStepSchema
